import React, { useEffect } from "react";
import { Link } from "react-router-dom";

export default function Paus() {
  const currentYear = new Date().getFullYear();
    useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-800 font-sans">

      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <img
              src="/PAUS Logo_Black.png"
              alt="PAUS"
              className="h-16 md:h-20 w-auto object-contain mb-8"
            />
            <p className="uppercase tracking-[0.25em] text-xs text-[#6ec87a] mb-4">
              Power of Now. Designed for Real Life.
            </p>
            <h1 className="text-4xl md:text-5xl leading-tight font-semibold mb-6 text-slate-800">
              Reset. Stabilize. Perform with Clarity.
            </h1>
            <p className="text-slate-600 text-base md:text-lg leading-8 mb-4">
              PAUS is a human resilience platform that helps individuals and organizations
              reset, stabilize, and perform with clarity — in real time.
            </p>
            <p className="text-slate-600 text-sm leading-7 mb-8">
              It brings together technology, wellness, and everyday experiences to make
              presence reliable, not accidental.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#how-it-works"
                className="bg-[#6ec87a] text-[#0a1628] font-semibold px-6 py-3 rounded-xl hover:opacity-90 transition"
              >
                How It Works
              </a>
              <Link
                to="/#contact"
                className="border border-slate-300 text-slate-700 px-6 py-3 rounded-xl hover:bg-slate-50 transition"
              >
                Contact Us
              </Link>
            </div>
          </div>

          {/* App Screenshots - Two phones side by side */}
          <div className="flex justify-center md:justify-end gap-4 items-start">
            <div className="relative mt-6">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 blur-2xl scale-110" />
              <img
                src="/ilo.png"
                alt="PAUS Login Screen"
                className="relative w-56 md:w-60 rounded-3xl shadow-2xl border border-slate-200"
              />
            </div>
            <div className="relative">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 blur-2xl scale-110" />
              <img
                src="/Main dashboard.png"
                alt="PAUS App Dashboard"
                className="relative w-56 md:w-60 rounded-3xl shadow-2xl border border-slate-200"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is PAUS */}
      <section className="bg-[#f6f9fc] border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-[#6ec87a] uppercase tracking-[0.2em] text-xs font-semibold mb-3">What is PAUS?</p>
            <h2 className="text-3xl md:text-4xl font-semibold mb-5">
              Not just an app. An integrated resilience ecosystem.
            </h2>
            <p className="text-slate-600 leading-8">
              PAUS is a digital and experiential ecosystem designed to support mental clarity,
              emotional balance, and cognitive stability. It is an integrated system of tools,
              environments, and experiences that help restore baseline before stress, overload,
              or poor decisions occur.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              { label: "Not just an app", desc: "A full ecosystem spanning digital tools, guided protocols, and real-world environments." },
              { label: "Not just content", desc: "Actionable, repeatable interventions embedded into your everyday workflow." },
              { label: "Not a one-time solution", desc: "A continuous resilience layer that works quietly alongside everything else." },
            ].map((item) => (
              <div key={item.label} className="bg-white border border-slate-200 rounded-2xl p-6">
                <p className="text-[#6ec87a] font-semibold text-sm mb-2">{item.label}</p>
                <p className="text-slate-600 text-sm leading-6">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How PAUS Works */}
      <section id="how-it-works" className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-[#6ec87a] uppercase tracking-[0.2em] text-xs font-semibold mb-3">How PAUS Works</p>
            <h2 className="text-3xl md:text-4xl font-semibold mb-5">
              Four core protocols. Embedded in your rhythm.
            </h2>
            <p className="text-slate-600 leading-8">
              PAUS operates through a software-driven platform integrated across mobile and
              digital environments, supported by intelligent systems and scalable architecture.
            </p>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 mb-10">
            {[
              { name: "Check-In", icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z", desc: "Start with intentional presence before any session or task begins." },
              { name: "Decision", icon: "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z", desc: "Clear cognitive noise before making critical decisions." },
              { name: "Reset", icon: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15", desc: "Restore baseline between high-demand tasks and transitions." },
              { name: "Check-Out", icon: "M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1", desc: "Close sessions cleanly — preventing carryover stress and mental residue." },
            ].map((p, i) => (
              <div key={p.name} className="bg-white border border-slate-200 rounded-2xl p-6 relative overflow-hidden">
                <span className="absolute top-4 right-4 text-5xl font-bold text-slate-100 select-none">0{i + 1}</span>
                <div className="w-10 h-10 rounded-xl bg-[#6ec87a]/15 flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-[#6ec87a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={p.icon} />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">{p.name}</h3>
                <p className="text-slate-600 text-sm leading-6">{p.desc}</p>
              </div>
            ))}
          </div>

          {/* Platform features */}
          <div className="bg-white border border-slate-200 rounded-2xl p-8">
            <p className="text-[#6ec87a] text-sm font-semibold mb-4">Platform Includes</p>
            <div className="flex flex-wrap gap-3">
              {["AI-powered tools & ILO chatbot", "Structured micro-protocols", "Real-time interaction systems", "ILO", "Stream", "Sleep", "Frequency", "World of PAUS", "Community"].map((f) => (
                <span key={f} className="bg-slate-100 border border-slate-200 text-slate-700 text-sm px-4 py-2 rounded-full">{f}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Wellness & Performance */}
      <section className="bg-[#f6f9fc] border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[#6ec87a] uppercase tracking-[0.2em] text-xs font-semibold mb-3">Wellness & Human Performance</p>
            <h2 className="text-3xl md:text-4xl font-semibold mb-6">
              Designed to support well-being without complexity
            </h2>
            <p className="text-slate-600 leading-8 mb-6">
              PAUS supports mental wellness, emotional resilience, and cognitive recovery
              through simple, repeatable interventions — for individuals and enterprise teams alike.
            </p>
            <p className="text-slate-600 leading-8 italic border-l-2 border-[#6ec87a]/40 pl-4">
              Most people are not breaking down — they are holding tension. PAUS helps release
              that tension before it turns into stress, conflict, or poor decisions.
            </p>
          </div>
          <div className="space-y-3">
            {[
              "Reduce stress and mental overload",
              "Improve focus and clarity",
              "Reset between tasks and decisions",
              "Maintain emotional balance in high-pressure environments",
            ].map((point) => (
              <div key={point} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl px-5 py-4">
                <div className="w-5 h-5 rounded-full bg-[#6ec87a]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-3 h-3 text-[#6ec87a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-slate-600 text-sm leading-6">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning + Enterprise + Technology */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-[#6ec87a] uppercase tracking-[0.2em] text-xs font-semibold mb-3">Platform & Services</p>
            <h2 className="text-3xl md:text-4xl font-semibold mb-5">Built for individuals and enterprises</h2>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
            {[
              {
                title: "Learning & Experiences",
                items: ["Audio and video sessions", "Micro-learning experiences", "Guided resets and reflective practices", "Interactive digital experiences"],
              },
              {
                title: "Enterprise Services",
                items: ["Subscription-based platform access", "Enterprise wellness solutions", "Workplace integration", "Scalable deployment across teams"],
              },
              {
                title: "Technology",
                items: ["AI-powered systems & Open API", "Scalable cloud-based architecture", "Human-centered interaction design", "Secure and privacy-first frameworks"],
              },
              {
                title: "Spaces & Extensions",
                items: ["Wellness spaces and reset zones", "Experience centers & retreats", "Apparel and lifestyle products", "Journals, guides, and workbooks"],
              },
            ].map((col) => (
              <div key={col.title} className="bg-white border border-slate-200 rounded-2xl p-6">
                <h3 className="text-base font-semibold text-[#6ec87a] mb-4">{col.title}</h3>
                <ul className="space-y-2">
                  {col.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-slate-600 text-sm leading-6">
                      <span className="text-[#6ec87a] mt-1">•</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#f6f9fc] border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 text-center">
          <img src="/PAUS Logo_Black.png" alt="PAUS" className="h-12 w-auto object-contain mx-auto mb-6 opacity-80" />
          <h2 className="text-3xl md:text-4xl font-semibold mb-3">Ready to Begin?</h2>
          <p className="text-slate-600 text-sm mb-8">No complexity. No overload. Just clarity.</p>
          <p className="text-slate-600 italic mb-8">Reset. Realign. Return to now.</p>
          <Link
            to="/#contact"
            className="inline-block bg-[#6ec87a] text-[#0a1628] font-semibold px-8 py-3 rounded-xl hover:opacity-90 transition"
          >
            Contact Us to Get Started
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#f6f9fc] border-t border-slate-200 text-slate-600">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-3">
            <img src="/PAUS Logo_White.png" alt="PAUS" className="h-7 w-auto object-contain" />
            <p>© {currentYear} PAUS. A product of <span className="text-[#6ec87a]">M3 Global Medicare LLP</span>.</p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/paus/terms-of-use" className="hover:text-[#6ec87a] transition-colors">Terms of Use</Link>
            <Link to="/paus/privacy-policy" className="hover:text-[#6ec87a] transition-colors">Privacy Policy</Link>
            <Link to="/" className="hover:text-[#6ec87a] transition-colors">← Back to M3 Global</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}